import { makeAutoObservable, runInAction } from 'mobx'
import { type ITabItem } from 'shared/ui'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { UsersApi } from 'entities/Users'
import { type IResponseMembersCounts } from 'entities/Users/api/types'
import { getManageableInboxes } from 'entities/Inbox'
import { ActiveMembersStore } from 'pages/settings/pages/members/pages/membersView/ui/ActiveMembersTab/store/activeMembersStore'
import { PendingMembersStore } from 'pages/settings/pages/members/pages/membersView/ui/PendingMembersTab/store/pendingMembersStore'
import { RequestsMembersStore } from 'pages/settings/pages/members/pages/membersView/ui/RequestsMembersTab/store/requestsMembersStore'

export type MembersTabKey = 'active' | 'pending' | 'requests'

export class MembersViewStore {
  private _activeMembersStore: ActiveMembersStore
  private _pendingMembersStore: PendingMembersStore
  private _requestsMembersStore: RequestsMembersStore
  private _membersCount = 0
  private _invitesCount = 0
  private _requestsCount: IResponseMembersCounts['requests'] = {
    total: 0,
    salesmsg_access: 0,
    inbox_access: 0,
    permissions_access: 0,
  }

  constructor(private _entities: IEntitiesStore) {
    this._activeMembersStore = new ActiveMembersStore(this._entities)
    this._pendingMembersStore = new PendingMembersStore()
    this._requestsMembersStore = new RequestsMembersStore(this._entities)
    makeAutoObservable(this)
  }

  fetchCounts = async () => {
    try {
      const {
        data: { members, invites, requests },
      } = await UsersApi.getMembersCounts()
      runInAction(() => {
        this._membersCount = members
        this._invitesCount = invites
        this._requestsCount = requests
      })
      this._requestsMembersStore.updateCounts(requests)
    } catch (error) {
      console.error('Failed to fetch members counts:', error)
    }
  }

  get tabs(): ITabItem<MembersTabKey>[] {
    return [
      {
        name: 'Active',
        key: 'active',
        count: this._membersCount,
      },
      {
        name: 'Pending',
        key: 'pending',
        count: this._invitesCount,
      },
      {
        name: 'Requests',
        key: 'requests',
        count: this._requestsCount.total,
      },
    ]
  }

  get activeMembersStore() {
    return this._activeMembersStore
  }

  get pendingMembersStore() {
    return this._pendingMembersStore
  }

  get requestsMembersStore() {
    return this._requestsMembersStore
  }

  get seatsUsed() {
    return this._entities.subscriptionStore.seatsUsed
  }

  get seatsTotal() {
    return this._entities.subscriptionStore.seatsTotal
  }

  get inboxesForInvite() {
    const inboxes = getManageableInboxes({
      inboxes: this._entities.inboxesStore.organizationInboxes,
      userId: this._entities.usersStore.user?.id,
      canManageOrganizationInboxes: this._entities.userPermissionsStore.getItem(
        'can_manage_organization_inboxes'
      ),
    })
    const isUKCountry = Boolean(this._entities.usersStore.user?.isUKCountry)

    if (this._entities.subscriptionStore.isTrial && isUKCountry) {
      return inboxes.filter((inbox) => !inbox.isTrialIdentifier)
    }

    return inboxes
  }

  dispose = () => {
    this._activeMembersStore.dispose()
    this._pendingMembersStore.dispose()
    this._requestsMembersStore.dispose()
  }
}
